import React from 'react';
import { Rocket, Check, ArrowRight, Users, ClipboardCheck, Target, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { trackCTAClick } from '../utils/analytics';

const programFeatures = [
  { icon: Users, text: "Designed for managers, teams, and organizations of all sizes" },
  { icon: ClipboardCheck, text: "Combines leadership training, assessments, and real-world application" },
  { icon: Calendar, text: "Flexible delivery: workshops, multi-session, or ongoing development" },
  { icon: Target, text: "Focused on measurable business outcomes—not just training hours" }
];

const SignatureProgram = () => {
  const scrollToConsultation = () => {
    trackCTAClick('Schedule a Leadership Strategy Session', 'signature_program');
    const element = document.querySelector('#consultation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="signature-program" className="py-16 sm:py-24 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
              <Rocket className="h-4 w-4 text-violet-400" />
              <span className="text-violet-400 text-sm font-semibold">Signature Program</span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Workforce Performance
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                Acceleration Program
              </span>
            </h2>

            <p className="text-slate-400 text-base sm:text-lg mb-8 leading-relaxed">
              Our flagship development program that transforms how your leaders communicate, 
              make decisions, and drive results. Designed for organizations serious about 
              measurable improvement.
            </p>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {programFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="h-5 w-5 text-violet-400" />
                    </div>
                    <span className="text-slate-300 text-sm sm:text-base pt-2">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={scrollToConsultation}
              data-testid="signature-program-cta-button"
              className="bg-violet-500 hover:bg-violet-400 text-white font-semibold px-6 py-5 transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/25"
            >
              Schedule a Leadership Strategy Session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Right Visual Card */}
          <div className="relative">
            <div className="p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-white/10">
              {/* Program Highlights */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                  <Rocket className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Accelerate Your Team's Performance
                </h3>
                <p className="text-slate-400 text-sm">
                  A comprehensive approach that creates lasting change
                </p>
              </div>

              {/* Program Components */}
              <div className="space-y-3 mb-8">
                {['Leadership Skills Development', 'DISC & Behavioral Assessments', 'Communication & Accountability', 'Real-World Application', 'Measurable Outcomes Tracking'].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                    <Check className="h-5 w-5 text-violet-400 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              {/* Funding Note */}
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-emerald-300 text-sm text-center">
                  Eligible organizations may qualify for workforce training funding or reimbursement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignatureProgram;
